import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Archive, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API } from '../config/api';

const ALL = '__all__';

const raceDay = (r) => {
  for (const raw of [r.date, r.created_at]) {
    const s = (raw || '').slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  }
  return null;
};

const isLive = (r) =>
  r.status === 'in_progress' || (r.source === 'speedhive_live_auto' && r.status !== 'finalized');

export const PurgeOldSessions = ({ races, categories, getClase, onDone }) => {
  const [open, setOpen] = useState(false);
  const [before, setBefore] = useState('');
  const [clase, setClase] = useState(ALL);
  const [busy, setBusy] = useState(false);

  const candidates = useMemo(() => {
    if (!before) return [];
    return races.filter((r) => {
      const day = raceDay(r);
      return day && day < before && !isLive(r) && (clase === ALL || getClase(r) === clase);
    });
  }, [races, before, clase, getClase]);

  const handleDelete = async () => {
    if (!window.confirm(`¿Estás seguro de eliminar ${candidates.length} sesiones? Esta acción no se puede deshacer.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/races/bulk-delete`,
        { ids: candidates.map((r) => r.id), before },
        { withCredentials: true },
      );
      toast.success(`${data.deleted} sesiones eliminadas`);
      setOpen(false);
      setBefore('');
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al eliminar sesiones');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        size="sm"
        className="h-8 border-red-900/50 text-red-400 hover:bg-red-950/50 hover:text-red-300 text-xs"
        data-testid="purge-old-sessions-btn"
      >
        <Archive className="w-3.5 h-3.5 mr-1.5" />
        Eliminar viejas
      </Button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="bg-neutral-950 border-red-900/40 text-white sm:max-w-md" data-testid="purge-old-sessions-dialog">
          <DialogHeader>
            <DialogTitle>Eliminar sesiones viejas</DialogTitle>
            <DialogDescription className="text-gray-400">
              Se borran las sesiones anteriores a la fecha elegida. Las sesiones en vivo o en curso nunca se borran.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <label className="block space-y-1.5">
              <span className="text-xs text-gray-400">Borrar sesiones anteriores al</span>
              <input
                type="date"
                value={before}
                onChange={(e) => setBefore(e.target.value)}
                className="w-full h-9 px-3 rounded-md bg-neutral-900 border border-neutral-700 text-sm text-white [color-scheme:dark] focus:border-red-600 focus:outline-none"
                data-testid="purge-before-date"
              />
            </label>
            <div className="space-y-1.5">
              <span className="text-xs text-gray-400">Categoría</span>
              <Select value={clase} onValueChange={setClase}>
                <SelectTrigger className="bg-neutral-900 border-neutral-700 text-white h-9" data-testid="purge-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-neutral-900 border-neutral-700">
                  <SelectItem value={ALL} className="text-white">Todas las categorías</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c} className="text-white">{c.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-gray-300" data-testid="purge-count">
              {before
                ? <>Se eliminarán <strong className="text-red-400">{candidates.length}</strong> sesiones.</>
                : 'Elegí una fecha para ver cuántas sesiones se eliminarán.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy} className="text-gray-300" data-testid="purge-cancel-btn">
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              disabled={busy || candidates.length === 0}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="purge-confirm-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-1.5" />Eliminar {candidates.length}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PurgeOldSessions;
